<template>
  <div>
    <div class="grid-column ai-c jc-s gap-10">
      <span class="sac-tag f-navy size-12 start">
        {{ instanceCount }}
      </span>
      <span class="sac__font-12 f-white semi-bold">for {{ alertInfo.title }}</span>
    </div>
    <div class="alert-list-container margin-top-16">
      <div v-if="instanceList.length" class="alert-list">
        <div
          v-for="(item, idx) in instanceList"
          :key="idx"
          class="grid-column ai-c jc-b"
        >
          <div class="sac-tag r-4 size-12 f-white">
            {{ formatStartTime(item.startTime) }}
          </div>
          <fragment>
            <div v-if="showPerformance">
              <div v-if="item.returnTemp && item.supplyTemp" class="grid-column ai-c">
                <div class="sac__font-12 f-white semi-bold">
                  {{ item.supplyTemp }}
                  <span class="sac__font-12 f-white med">Supply &ndash; &nbsp;</span>
                </div>
                <div class="sac__font-12 f-white semi-bold">
                  {{ item.returnTemp }}
                  <span class="sac__font-12 f-white med">&nbsp;Return</span>
                </div>
              </div>
              <div v-else class="sac__font-14 semi-bold f-white">
                -
              </div>
            </div>
            <div v-else-if="showLocation">
              <span class="sac__font-12 f-white semi-bold">Location: </span>
              <span class="sac__font-12 f-white med">{{ item.location }}</span>
            </div>
          </fragment>
          <fragment>
            <div v-if="item.type === 'Water Detected'" class="sac-tag r-4 size-12 tomato">
              {{ item.title }}
            </div>
            <div v-else class="sac-tag r-4 size-12 tomato">
              {{ getAlertCauseValue(item) }}
            </div>
          </fragment>
        </div>
      </div>
      <div v-else class="sac__font-14 n20 center">
        No alerts to show
      </div>
      <div class="margin-top-16">
        <button
          v-if="alertList.length > 3 && !showMore"
          class="more-btn"
          type="button"
          @click="toggleAllAlerts"
        >
          <span>{{ alertList.length - 3 }} MORE</span>
          <img
            src="@shared/assets/img/general/chevron-white-down.png"
            class="img-size-20"
            alt="more"
          />
        </button>
        <button
          v-else-if="alertList.length > 3 && showMore"
          class="more-btn"
          type="button"
          @click="toggleAllAlerts"
        >
          <span>Hide</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import serviceMixin from '@shared/mixins/serviceMixin';
import convertToCelsiusResult from '@shared/lib/converter/convertToCelsiusResult';

export default {
  name: 'AlertInstanceList',
  mixins: [serviceMixin, unitPreferencesMixin],
  props: {
    alert: {
      type: Object,
      default: () => {},
    },
    alertList: {
      type: Array,
      default: () => [],
    },
    cycleList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showMore: false,
    };
  },
  computed: {
    alertInfo() {
      if (!this.alert) {
        return {};
      }
      return this.alert;
    },
    showLocation() {
      const {
        type,
      } = this.alertInfo;
      switch (type) {
        case 'Water Detected':
          return true;
        case 'Cool Supply Air':
          return true;
        case 'Heat Supply Air':
          return true;
        case 'Frozen Coil':
          return true;
        case 'Trend Cool Runtime':
          return true;
        case 'Trend Heat Runtime':
          return true;
        case 'Cool Runtime':
          return true;
        case 'Failure to Maintain Return Air':
          return true;
        default:
          return false;
      }
    },
    showPerformance() {
      const {
        type,
      } = this.alertInfo;
      switch (type) {
        case 'Cool Delta T':
          return true;
        case 'Heat Delta T':
          return true;
        default:
          return false;
      }
    },
    showSupplyTemp() {
      const {
        type,
      } = this.alertInfo;
      switch (type) {
        case 'Cool Supply Air':
          return true;
        case 'Heat Supply Air':
          return true;
        default:
          return false;
      }
    },
    showReturnTemp() {
      const {
        type,
      } = this.alertInfo;
      switch (type) {
        case 'Failure to Maintain Return Air':
          return true;
        default:
          return false;
      }
    },
    instanceList() {
      if (!this.showMore) {
        return this.alertList.slice(0, 3).map((alert, idx) => {
          const performance = this.getPerformance(alert, idx);
          return {
            ...alert,
            supplyTemp: performance.supplyTemp,
            returnTemp: performance.returnTemp,
          };
        });
      }
      return this.alertList.map((alert, idx) => {
        const performance = this.getPerformance(alert, idx);
        return {
          ...alert,
          supplyTemp: performance.supplyTemp,
          returnTemp: performance.returnTemp,
        };
      });
    },
    instanceCount() {
      const count = this.alertList.length;
      if (count === 1) {
        return `${count} INSTANCE`;
      }
      return `${count} INSTANCES`;
    },
  },
  methods: {
    toggleAllAlerts() {
      this.showMore = !this.showMore;
    },
    getDtPayload(value) {
      if (!value) {
        return { sup: 0, ret: 0 };
      }
      const dt = JSON.parse(value);
      if (dt) {
        return {
          sup: dt.sup,
          ret: dt.ret,
        };
      }
      return { sup: 0, ret: 0 };
    },
    getPerformance(alert, idx) {
      const {
        type,
        alertDetails,
      } = alert;
      const { sup, ret } = this.getDtPayload(alertDetails);
      let supplyTemp = sup;
      let returnTemp = ret;
      const alertCycle = this.cycleList[idx];
      if (alertCycle && !supplyTemp && !returnTemp) {
        if (alertCycle.dtPayload) {
          const {
            dtPayload,
          } = alertCycle;
          supplyTemp = dtPayload.sup;
          returnTemp = dtPayload.ret;
        }
      }
      if (this.tempFormat === '°C') {
        supplyTemp = convertToCelsiusResult(supplyTemp);
        returnTemp = convertToCelsiusResult(returnTemp);
      }
      supplyTemp = supplyTemp ? `${supplyTemp}${this.tempFormat}` : 0;
      returnTemp = returnTemp ? `${returnTemp}${this.tempFormat}` : 0;
      return {
        supplyTemp,
        returnTemp,
        type: type.toLowerCase(),
      };
    },
    formatStartTime(cycleDate) {
      return moment(cycleDate).format('MMM D, YY h:mm A');
    },
    getAlertCauseValue(a) {
      const {
        type,
        boundaryValue,
      } = a;
      if (this.showPerformance) {
        return boundaryValue ? `${this.tempFormat !== '°F' ? (convertToCelsiusResult(boundaryValue)).toFixed(1) : boundaryValue.toFixed(1)}${this.tempFormat} Performance` : '--';
      }
      if (this.showSupplyTemp) {
        return boundaryValue ? `${this.tempFormat !== '°F' ? (convertToCelsiusResult(boundaryValue)).toFixed(1) : boundaryValue.toFixed(1)}${this.tempFormat} Supply air temp` : '--';
      }
      if (this.showReturnTemp) {
        return boundaryValue ? `${this.tempFormat !== '°F' ? (convertToCelsiusResult(boundaryValue)).toFixed(1) : boundaryValue.toFixed(1)}${this.tempFormat} Return air temp` : '--';
      }
      if (type.toLowerCase().includes('trend')) {
        return `${(boundaryValue / 60).toFixed(2)} hrs Run Time`;
      }
      if (type.toLowerCase().includes('runtime')) {
        return `${(boundaryValue).toFixed(2)} Run Time`;
      }
      return '--';
    },
  },
};
</script>

<style lang="scss" scoped>
.alert-list-container {
  background-color: #404b67;
  border-radius: 6px;
  padding: 16px;
  .alert-list {
    display: grid;
    gap: 1rem;
    max-height: 135px;
    overflow: auto;
  }
  .more-btn {
    border: 1px solid $WHITE;
    border-radius: 6px;
    padding: 3px 8px;
    font-size: 12px;
    font-weight: 600;
    color: $WHITE;
    grid-auto-flow: column;
    align-items: center;
    justify-self: flex-start;
    display: grid;
  }
}
</style>
